import React, { useContext, useState, useEffect, useRef } from "react";
import { Flex, Layout, Typography, Image, Select, Avatar, Space } from "antd";
import { MenuOutlined } from "@ant-design/icons";
import typoStyles from "../../../styles/Custom/Typography.module.css";
import UserSettings from "./UserSettings";
import ReceptionistMobileDrawer from "./ReceptionistMobileDrawer";
import { UserContext } from "../../../context/UserContext";
import { getCookie, setCookie } from "../../../helpers/localStorage";
import { useLocation } from "react-router-dom";
import { FacilityID, IS_HOD, Role } from "../../../constants/defaultKeys";
import "./CustomLayout.css";
import { redirectToDashboard } from "../../../helpers/auth/helper";
import { getRoleLabelWithFacility } from "../../../helpers/getRoleLabel";
import useIsMobile from "../../../hooks/useIsMobile";

const { Header, Footer, Content, Sider } = Layout;
const { Option } = Select;

/*
 * Roles that get the role/facility switcher in the header. Anyone holding more
 * than one role needs a way to move between them without logging out.
 */
const ROLE_SWITCHER_ROLES = [
  "Receptionist",
  "Facility_Admin",
  "Pharmacist",
  "Organization_Admin",
  "Organization_Owner",
];

const SIDER_STATE_KEY = "curengo:siderCollapsed";

const readSiderPreference = () => {
  try {
    const raw = window.localStorage.getItem(SIDER_STATE_KEY);
    return raw === null ? null : raw === "1";
  } catch {
    return null;
  }
};

const writeSiderPreference = (collapsed) => {
  try {
    window.localStorage.setItem(SIDER_STATE_KEY, collapsed ? "1" : "0");
  } catch {
  }
};

const SIDER_EXPANDED_BY_DEFAULT_ROLES = [
  "Receptionist",
  "Facility_Admin",
  "Organization_Admin",
  "Organization_Owner",
];

const CommonLayout = ({
  children,
  header,
  footer,
  sideContent,
  headerExtra = true,
  headerSearch = null,
  patientEmrMode = false,
  layoutClassName = "",
}) => {
  const location = useLocation();
  const { userDetails, setUserDetails } = useContext(UserContext);
  const isDesktop = !useIsMobile(1200);
  const hideRoleDropdownOnOrgProfile = location?.pathname?.startsWith(
    "/organization-settings/"
  );
  const disableSiderCollapseOnOrgProfile = hideRoleDropdownOnOrgProfile;

  const [collapsed, setCollapsed] = useState(() => {
    if (disableSiderCollapseOnOrgProfile) return false;
    if (!isDesktop) return true;
    const remembered = readSiderPreference();
    if (remembered !== null) return remembered;
    const initialRole = userDetails?.currRole || getCookie(Role);
    return !SIDER_EXPANDED_BY_DEFAULT_ROLES.includes(initialRole);
  });

  const applyCollapsed = (next) => {
    setCollapsed(next);
    writeSiderPreference(next);
  };
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(
    Number(getCookie(FacilityID)) || userDetails?.currFacility
  );
  const [selectedRole, setSelectedRole] = useState(
    getCookie(Role) || userDetails?.activeOrg?.roles?.[0]?.role
  );

  const handleRoleChange = (value) => {
    const lastUnderscoreIndex = value.lastIndexOf("_");

    // Extract role by slicing up to the last underscore
    const role = value.slice(0, lastUnderscoreIndex);

    // Extract facility ID by slicing after the last underscore
    const facility_id = value.slice(lastUnderscoreIndex + 1);

    const is_hod = userDetails?.activeOrg?.roles?.find(
      (record) =>
        record?.role === role && record?.facility_id === Number(facility_id)
    )?.is_hod;
    const roleObj = userDetails?.activeOrg?.roles?.find(
      (record) =>
        record.role === role && record.facility_id === Number(facility_id)
    );

    redirectToDashboard(userDetails?.activeOrg?.domain, role, roleObj);
    setSelectedRole(value);
    setSelectedFacility(facility_id);
    setCookie(Role, role, `.${process.env.REACT_APP_PUBLIC_DOMAIN}`, "/");
    setCookie(IS_HOD, is_hod, `.${process.env.REACT_APP_PUBLIC_DOMAIN}`, "/");
    setCookie(
      FacilityID,
      facility_id,
      `.${process.env.REACT_APP_PUBLIC_DOMAIN}`,
      "/"
    );
    setUserDetails((prev) => ({
      ...prev,
      currRole: role,
      currFacility: facility_id,
    }));
  };
  const facilityVisible = userDetails?.currRole || getCookie(Role);
  const isReceptionist = facilityVisible === "Receptionist";
  const useMobileNav = isReceptionist && Boolean(sideContent);
  const layoutRootClass = [
    "layoutContent",
    layoutClassName,
    useMobileNav ? "reception-layout" : "",
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    if (disableSiderCollapseOnOrgProfile) {
      setCollapsed(false);
      return;
    }
    if (isDesktop) return;
    setCollapsed((prev) => !prev);
  }, []);

  const lastRoleRef = useRef(facilityVisible);
  useEffect(() => {
    if (!isDesktop || disableSiderCollapseOnOrgProfile) return;

    const roleChanged = lastRoleRef.current !== facilityVisible;
    lastRoleRef.current = facilityVisible;
    if (!roleChanged && readSiderPreference() !== null) return;

    applyCollapsed(!SIDER_EXPANDED_BY_DEFAULT_ROLES.includes(facilityVisible));
  }, [isDesktop, facilityVisible, disableSiderCollapseOnOrgProfile]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  const innerLayoutClass = sideContent
    ? collapsed
      ? "contentPushWithSideandCollapse"
      : "contentPushWithSideandNoCollapse"
    : "contentPushWithoutSide";

  return (
    <Layout className={layoutRootClass}>
      {sideContent && (
        <Sider
          className={`${collapsed ? "siderCollapse" : "siderNoCollapse"} ${
            useMobileNav ? "reception-sider-desktop" : ""
          }`}
          collapsed={collapsed}
          onCollapse={(value, type) => {
            if (type === "responsive") return;
            applyCollapsed(value);
          }}
          collapsible={!disableSiderCollapseOnOrgProfile}
          /* Every rail folds from antd's own control at its foot, the pharmacy
             included - it briefly drew a disc on its right edge instead. */
          breakpoint="md"
        >
          <Flex
            style={{
              height: "64px",
              flexShrink: 0,
              justifyContent: collapsed ? "center" : "flex-start",
              alignItems: "center",
              paddingInline: "24px",
            }}
          >
            <Typography
              className={collapsed ? typoStyles.subtitle2 : typoStyles.h6}
            >
              {userDetails?.activeOrg?.org_logo ? (
                <Image
                  preview={false}
                  src={userDetails?.activeOrg?.org_logo}
                  alt={"Logo"}
                  style={{
                    width: "50px",
                    marginTop: "10px",
                  }}
                />
              ) : collapsed ? (
                <Avatar
                  size={45}
                  style={{
                    fontSize: "20px",
                    background: userDetails?.activeOrg?.color,
                  }}
                >
                  {userDetails?.activeOrg?.org_name?.slice(0, 1).toUpperCase()}
                </Avatar>
              ) : (
                userDetails?.activeOrg?.org_name
              )}
            </Typography>
          </Flex>
          <div
            className="sideMenuScrollArea"
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              paddingBlock: "24px",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {React.cloneElement(sideContent, { collapsed })}
          </div>
        </Sider>
      )}
      <Layout className={innerLayoutClass}>
        {header && (
          <Header
            className={`headerStyle ${
              sideContent
                ? collapsed
                  ? "headerWithSideAndCollapse"
                  : "headerWithSideAndNoCollapse"
                : "headerStylesWithoutSide"
            }`}
          >
            {useMobileNav ? (
              <div className="reception-mobile-header-bar">
                <div className="mobile-only reception-mobile-header-start">
                  <Space size={8}>
                    <MenuOutlined
                      style={{ fontSize: 18, cursor: "pointer" }}
                      onClick={() => setMenuOpen(true)}
                    />
                    <Image
                      preview={false}
                      src={userDetails?.activeOrg?.org_logo}
                      style={{ width: 28 }}
                    />
                  </Space>
                </div>
                <div className="mobile-only reception-mobile-header-title">
                  {header}
                </div>
                <div
                  className="desktop-only flex-between"
                  style={{ height: "64px", paddingInline: "24px", gap: "24px" }}
                >
                  <div className="tabWebDisplay">{header}</div>
                  {headerSearch}
                  {headerExtra && (
                    <div className="flex-end header-actions" style={{ gap: "24px" }}>
                      {ROLE_SWITCHER_ROLES.includes(facilityVisible) &&
                        !hideRoleDropdownOnOrgProfile && (
                          <Select
                            className="header-role-select"
                            value={`${selectedRole}_${selectedFacility}`}
                            onChange={handleRoleChange}
                            style={{ width: "350px" }}
                          >
                            {userDetails?.activeOrg?.roles?.map(
                              (role) =>
                                role?.role !== "Partner" && (
                                  <Option
                                    value={`${role?.role}_${role?.facility_id}`}
                                    key={`${role?.role}_${role?.facility_id}`}
                                  >
                                    {getRoleLabelWithFacility(role)}
                                  </Option>
                                )
                            )}
                          </Select>
                        )}
                      <UserSettings />
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div
                className="flex-between"
                style={{ height: "64px", paddingInline: "24px", gap: "24px" }}
              >
                <div className="tabWebDisplay">{header}</div>
                {headerSearch}
                {headerExtra && (
                  <div className="flex-end header-actions" style={{ gap: "24px" }}>
                    {ROLE_SWITCHER_ROLES.includes(facilityVisible) &&
                      !hideRoleDropdownOnOrgProfile && (
                        <Select
                          className="header-role-select"
                          value={`${selectedRole}_${selectedFacility}`}
                          onChange={handleRoleChange}
                          style={{ width: "350px" }}
                        >
                          {userDetails?.activeOrg?.roles?.map(
                            (role) =>
                              role?.role !== "Partner" && (
                                <Option
                                  value={`${role?.role}_${role?.facility_id}`}
                                  key={`${role?.role}_${role?.facility_id}`}
                                >
                                  {getRoleLabelWithFacility(role)}
                                </Option>
                              )
                          )}
                        </Select>
                      )}
                    <UserSettings />
                  </div>
                )}
              </div>
            )}
          </Header>
        )}

        <Content
          className={`${
            header ? "contentStyle" : "contentNoHeaderStyle"
          }${patientEmrMode ? " contentPatientEmrMode" : ""}`}
          style={{
            paddingBottom: footer ? "132px" : layoutClassName?.includes("reception-layout") ? "8px" : "32px",
          }}
        >
          {children}
        </Content>
        {footer && (
          <Footer
            className={`footerStyle ${
              sideContent
                ? collapsed
                  ? "footerStyleWithCollapsedSide"
                  : "footerStyleWithNOCollapsedSide"
                : "footerStyleWithOutSide"
            }`}
          >
            {footer}
          </Footer>
        )}
      </Layout>

      {useMobileNav && menuOpen && (
        <ReceptionistMobileDrawer
          open={menuOpen}
          setOpen={setMenuOpen}
          sideContent={sideContent}
          onRoleChange={handleRoleChange}
          selectedRole={selectedRole}
          selectedFacility={selectedFacility}
          hideRoleDropdown={hideRoleDropdownOnOrgProfile}
        />
      )}
    </Layout>
  );
};

export default CommonLayout;
